import { useState, type ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import { toast } from 'sonner'
import { ChevronDown, History, Minus, MoveRight, Plus, Trash2 } from 'lucide-react'
import {
  useAdjustStock,
  useMoveStock,
  useRemoveStock,
  useStock,
  useUpdateStock,
  useWarehouses,
  type StockCondition,
  type UsedGrade,
  type WarehouseStockDetail,
  type WarehouseStockUpdate,
} from '@/api/warehouse'
import { ActionPopover } from '@/components/action-popover'
import { BackButton } from '@/components/back-button'
import { ChoicePicker, type PickerChoice } from '@/components/choice-picker'
import { InlineFieldGroup, InlineFieldInput, type InlineGroupField } from '@/components/inline-field-group'
import { POInlineEditableField } from '@/components/po-inline-edit'
import { POLineItemProductCell } from '@/components/po-line-item-product-cell'
import { StatusPill } from '@/components/status-pill'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { formatTimelineDate } from '@/components/timeline'
import { formatApiErrorMessage } from '@/lib/api-error'
import { usePageTitle } from '@/lib/page-title'
import { cn, formatDateOnly, formatMoney } from '@/lib/utils'
import {
  CONDITION_OPTIONS,
  GRADE_OPTIONS,
  PACKAGING_OPTIONS,
  casesNote,
  conditionLabel,
  conditionTone,
  describeEvent,
  itemLink,
  locationLabel,
  packagingLabel,
  quantityLabel,
  stockStatusPill,
} from './model'

type PlaceKey = 'bay' | 'pallet'
const PLACE_FIELDS: ReadonlyArray<InlineGroupField<PlaceKey>> = [
  { key: 'bay', label: 'Bay', className: 'col-span-3' },
  { key: 'pallet', label: 'Pallet', className: 'col-span-3' },
]

// One pill picks condition and grade together: "Used (Good)" is one choice.
const CONDITION_CHOICES: PickerChoice[] = CONDITION_OPTIONS.flatMap((option) =>
  option.value === 'used'
    ? GRADE_OPTIONS.map((grade) => ({
        value: `used:${grade.value}`,
        label: conditionLabel('used', grade.value),
        tone: conditionTone('used'),
      }))
    : [{ value: option.value, label: option.label, tone: conditionTone(option.value) }],
)

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="flex min-h-8 items-center text-xs text-muted-foreground">{label}</dt>
      <dd className="flex min-h-8 min-w-0 items-center break-words text-sm font-medium">{children}</dd>
    </>
  )
}

function useSaveField(stock: WarehouseStockDetail) {
  const update = useUpdateStock(stock.uuid)
  const save = (body: WarehouseStockUpdate, what: string) =>
    update.mutate(body, { onError: (error) => toast.error(formatApiErrorMessage(`${what} not saved`, error)) })
  return { save, pending: update.isPending }
}

function AdjustCount({ stock }: { stock: WarehouseStockDetail }) {
  const adjust = useAdjustStock(stock.uuid)
  const [direction, setDirection] = useState<1 | -1>(-1)
  const [amount, setAmount] = useState('1')
  const count = Number(amount)
  const valid = Number.isInteger(count) && count > 0
  return (
    <ActionPopover
      trigger={
        <Button variant="outline" size="sm" className="h-8 gap-1">
          <Plus className="size-3.5" aria-hidden />
          <Minus className="-ml-1 size-3.5" aria-hidden />
          Count
        </Button>
      }
      title="Change the count"
      align="end"
      note={{ label: 'Why the count changed', placeholder: 'Why (sold at the dock, recounted...)', minLength: 1 }}
      focusNote={false}
      confirmLabel={direction < 0 ? `Take away ${valid ? count : ''}`.trim() : `Add ${valid ? count : ''}`.trim()}
      canConfirm={valid}
      pending={adjust.isPending}
      onConfirm={(note, close) =>
        adjust.mutate(
          { quantity_delta: direction * count, note },
          {
            onSuccess: () => {
              setAmount('1')
              close()
            },
            onError: (error) => toast.error(formatApiErrorMessage('Count not changed', error)),
          },
        )
      }
    >
      <div className="flex items-center gap-2">
        <div role="group" aria-label="Add or take away" className="inline-grid grid-cols-2 rounded-lg border bg-muted/40 p-0.5">
          {([
            [-1, 'Take away', Minus],
            [1, 'Add', Plus],
          ] as const).map(([value, label, Icon]) => (
            <button
              key={value}
              type="button"
              aria-pressed={direction === value}
              aria-label={label}
              onClick={() => setDirection(value)}
              className={cn(
                'inline-flex size-8 items-center justify-center rounded-md text-muted-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50',
                direction === value && 'bg-background text-foreground shadow-sm',
              )}
            >
              <Icon className="size-4" aria-hidden />
            </button>
          ))}
        </div>
        <InlineFieldInput
          type="number"
          inputMode="numeric"
          min={1}
          step={1}
          value={amount}
          onChange={(event) => setAmount(event.target.value)}
          aria-label="How many"
          className="w-24 tabular-nums"
        />
      </div>
    </ActionPopover>
  )
}

function RemoveLot({ stock }: { stock: WarehouseStockDetail }) {
  const remove = useRemoveStock(stock.uuid)
  return (
    <ActionPopover
      trigger={
        <Button variant="ghost" size="icon" className="size-8 text-muted-foreground" aria-label="Remove this stock" title="Remove">
          <Trash2 className="size-4" aria-hidden />
        </Button>
      }
      title="Remove this stock"
      align="end"
      note={{ label: 'Why it is removed', placeholder: 'Why (entered twice, scrapped...)', minLength: 1 }}
      confirmLabel="Remove"
      confirmVariant="destructive"
      pending={remove.isPending}
      onConfirm={(note, close) =>
        remove.mutate(note, {
          onSuccess: close,
          onError: (error) => toast.error(formatApiErrorMessage('Not removed', error)),
        })
      }
    />
  )
}

function StockHistory({ stock }: { stock: WarehouseStockDetail }) {
  const [open, setOpen] = useState(false)
  return (
    <section aria-label="History">
      <Button
        variant="ghost"
        size="sm"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className="-ml-2 h-8 gap-1.5 px-2 text-muted-foreground"
      >
        <History className="size-3.5" aria-hidden />
        History
        <ChevronDown className={cn('size-3.5 transition-transform', open && 'rotate-180')} aria-hidden />
      </Button>
      {open && (
        <ol className="mt-2 space-y-3 border-l pl-4">
          {(stock.events ?? []).map((event) => {
            const { title, detail } = describeEvent(event)
            return (
              <li key={event.id} className="relative text-sm">
                <span className="absolute -left-[21px] top-1.5 size-2 rounded-full bg-border" aria-hidden />
                <p>
                  <span className="font-medium">{title}</span>
                  {event.purchase_order && (
                    <>
                      {' for '}
                      <Link
                        to="/purchase-orders/$poId"
                        params={{ poId: event.purchase_order.uuid }}
                        className="text-primary hover:underline"
                      >
                        PO #{event.purchase_order.po_number}
                      </Link>
                    </>
                  )}
                </p>
                {detail && <p className="text-muted-foreground">{detail}</p>}
                <p className="text-xs text-muted-foreground">
                  {formatTimelineDate(event.created_at)}
                  {event.actor ? ` · ${event.actor}` : ''}
                </p>
              </li>
            )
          })}
        </ol>
      )}
    </section>
  )
}

/** Move some of this place's units elsewhere; they become their own place with the same receipt. */
function MoveSome({ stock }: { stock: WarehouseStockDetail }) {
  const move = useMoveStock(stock.uuid)
  const [amount, setAmount] = useState('1')
  const [bay, setBay] = useState('')
  const [pallet, setPallet] = useState('')
  const count = Number(amount)
  const valid = Number.isInteger(count) && count > 0 && count <= stock.quantity_available && Boolean(bay.trim() || pallet.trim())
  return (
    <ActionPopover
      trigger={
        <Button variant="ghost" size="sm" className="-mr-2 h-7 gap-1 px-2 text-xs text-muted-foreground">
          <MoveRight className="size-3.5" aria-hidden />
          Move some
        </Button>
      }
      title="Move some to another place"
      align="end"
      confirmLabel={valid ? `Move ${count}` : 'Move'}
      canConfirm={valid}
      pending={move.isPending}
      onConfirm={(_note, close) =>
        move.mutate(
          { quantity: count, bay: bay.trim() || null, pallet: pallet.trim() || null },
          {
            onSuccess: () => {
              setAmount('1')
              setBay('')
              setPallet('')
              close()
            },
            onError: (error) => toast.error(formatApiErrorMessage('Not moved', error)),
          },
        )
      }
    >
      <div className="grid w-full grid-cols-[5rem_minmax(0,1fr)_minmax(0,1fr)] gap-2 sm:w-72">
        <InlineFieldInput
          type="number"
          inputMode="numeric"
          min={1}
          max={stock.quantity_available}
          step={1}
          value={amount}
          onChange={(event) => setAmount(event.target.value)}
          aria-label={`How many (of ${stock.quantity_available})`}
          className="tabular-nums"
        />
        <InlineFieldInput value={bay} onChange={(event) => setBay(event.target.value)} aria-label="To bay" placeholder="Bay" />
        <InlineFieldInput
          value={pallet}
          onChange={(event) => setPallet(event.target.value)}
          aria-label="To pallet"
          placeholder="Pallet"
        />
      </div>
    </ActionPopover>
  )
}

function DetailsCard({ stock, showWarehouse }: { stock: WarehouseStockDetail; showWarehouse: boolean }) {
  const { save, pending } = useSaveField(stock)
  const [editingPlace, setEditingPlace] = useState(false)
  const update = useUpdateStock(stock.uuid)
  return (
    <Card className="gap-0 py-0">
      <CardContent className="space-y-4 p-4">
        <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Details</h3>
        <section aria-label="Location" className="space-y-1">
          <div className="flex items-center justify-between gap-2">
            <h4 className="text-xs text-muted-foreground">Location</h4>
            {stock.status === 'active' && stock.quantity_available > 1 && <MoveSome stock={stock} />}
          </div>
          <InlineFieldGroup
            label="Location"
            fields={PLACE_FIELDS}
            values={{ bay: stock.bay ?? '', pallet: stock.pallet ?? '' }}
            editing={editingPlace}
            onEditingChange={setEditingPlace}
            pending={update.isPending}
            onSave={(next) =>
              update.mutate(
                { bay: next.bay.trim() || null, pallet: next.pallet.trim() || null },
                {
                  onSuccess: () => setEditingPlace(false),
                  onError: (error) => toast.error(formatApiErrorMessage('Location not saved', error)),
                },
              )
            }
          >
            <span className="block text-sm font-medium">
              {locationLabel(stock) ?? <span className="font-normal text-muted-foreground">Set bay and pallet</span>}
            </span>
            {showWarehouse && <span className="block text-xs text-muted-foreground">{stock.warehouse.name}</span>}
          </InlineFieldGroup>
        </section>

        <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 border-t pt-3">
          <Fact label="Received">
            <span className="px-2">{formatDateOnly(stock.received_at)}</span>
          </Fact>
          <Fact label="Net each">
            <POInlineEditableField
              kind="money"
              value={stock.unit_net ?? ''}
              displayValue={stock.unit_net != null ? formatMoney(stock.unit_net) : undefined}
              placeholder="Add what we paid"
              ariaLabel="Net each"
              pending={pending}
              buttonClassName="tabular-nums"
              onConfirm={(raw) => save({ unit_net: raw.trim() === '' ? null : raw.trim() }, 'Net')}
            />
          </Fact>
          <Fact label="Packaging">
            <POInlineEditableField
              kind="select"
              value={stock.packaging ?? ''}
              displayValue={packagingLabel(stock.packaging) ?? undefined}
              placeholder="Add packaging"
              options={[...PACKAGING_OPTIONS]}
              ariaLabel="Packaging"
              pending={pending}
              onConfirm={(raw) =>
                save({ packaging: (raw || null) as WarehouseStockUpdate['packaging'] }, 'Packaging')
              }
            />
          </Fact>
          {stock.created_by && (
            <Fact label="Received by">
              <span className="px-2">{stock.created_by}</span>
            </Fact>
          )}
        </dl>

        <div className="space-y-2 border-t pt-3">
          <div>
            <h4 className="text-xs text-muted-foreground">Packing notes</h4>
            <POInlineEditableField
              kind="textarea"
              value={stock.packing_notes ?? ''}
              placeholder="Add packing notes"
              ariaLabel="Packing notes"
              pending={pending}
              allowWrap
              buttonClassName="-ml-2 w-full justify-start"
              onConfirm={(raw) => save({ packing_notes: raw.trim() || null }, 'Packing notes')}
            />
          </div>
          <div>
            <h4 className="text-xs text-muted-foreground">Notes</h4>
            <POInlineEditableField
              kind="textarea"
              value={stock.notes ?? ''}
              placeholder="Add a note"
              ariaLabel="Notes"
              pending={pending}
              allowWrap
              buttonClassName="-ml-2 w-full justify-start"
              onConfirm={(raw) => save({ notes: raw.trim() || null }, 'Notes')}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

function Quantities({ stock }: { stock: WarehouseStockDetail }) {
  const perCase = stock.product.units_per_case
  const cells: Array<[string, number]> = [
    ['On hand', stock.quantity_on_hand],
    ['Reserved', stock.quantity_reserved],
    ['Available', stock.quantity_available],
  ]
  const note = casesNote(stock.quantity_on_hand, perCase)
  return (
    <div className="border-t pt-3">
      <dl className="grid grid-cols-3 gap-2">
        {cells.map(([label, value]) => (
          <div key={label}>
            <dt className="text-xs text-muted-foreground">{label}</dt>
            <dd className="text-xl font-semibold tabular-nums">{quantityLabel(value, perCase)}</dd>
          </div>
        ))}
      </dl>
      {note && <p className="mt-1 text-xs text-muted-foreground">{note}</p>}
    </div>
  )
}

export function StockDetailPage({ stockId }: { stockId: string }) {
  const { data: stock, isLoading, isError } = useStock(stockId)
  const warehouses = useWarehouses().data ?? []
  usePageTitle(stock ? `${stock.product.model} · Warehouse` : null)

  if (isLoading) {
    return (
      <div className="mx-auto max-w-[1600px] space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-48 w-full" />
      </div>
    )
  }
  if (isError || !stock) {
    return <p className="py-12 text-center text-muted-foreground">This stock was not found.</p>
  }
  return <StockDetail stock={stock} showWarehouse={warehouses.length > 1} />
}

function StockDetail({ stock, showWarehouse }: { stock: WarehouseStockDetail; showWarehouse: boolean }) {
  const update = useUpdateStock(stock.uuid)
  const status = stockStatusPill(stock)
  const removed = stock.status === 'removed'
  const conditionValue = stock.condition === 'used' ? `used:${stock.used_grade}` : stock.condition

  const changeCondition = (value: string) => {
    const [condition, grade] = value.split(':') as [StockCondition, UsedGrade | undefined]
    update.mutate(
      { condition, used_grade: grade ?? null },
      { onError: (error) => toast.error(formatApiErrorMessage('Condition not changed', error)) },
    )
  }

  return (
    <div className="mx-auto min-w-0 max-w-[1600px]">
      <nav className="flex min-w-0 items-center gap-1 text-sm text-muted-foreground">
        <BackButton iconOnly fallback={{ to: '/warehouse' }} fallbackLabel="Back to Warehouse" />
        <Link to="/warehouse" className="hover:text-foreground hover:underline">
          Warehouse
        </Link>
        <span aria-hidden>/</span>
        <Link {...itemLink(stock)} className="min-w-0 truncate hover:text-foreground hover:underline">
          {stock.product.model}
        </Link>
      </nav>

      <div className="mb-5 mt-1 flex flex-wrap items-center gap-2 lg:justify-between">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <h1 className="break-words text-lg font-bold tracking-tight sm:text-xl md:text-2xl">{stock.product.model}</h1>
          <ChoicePicker
            title="Condition"
            label={`Condition: ${conditionLabel(stock.condition, stock.used_grade)}. Change condition`}
            value={conditionValue}
            choices={CONDITION_CHOICES}
            onSelect={changeCondition}
            pending={update.isPending}
            disabled={removed}
          >
            <StatusPill tone={conditionTone(stock.condition)}>{conditionLabel(stock.condition, stock.used_grade)}</StatusPill>
          </ChoicePicker>
          {status && <StatusPill tone={status.tone}>{status.label}</StatusPill>}
        </div>
        {!removed && (
          <div className="flex items-center gap-1">
            <AdjustCount stock={stock} />
            <RemoveLot stock={stock} />
          </div>
        )}
      </div>

      {/* Below lg the columns dissolve and `order` puts the lot's place right after the product. */}
      <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="min-w-0 max-lg:contents lg:space-y-6">
          <Card className="gap-0 py-0 max-lg:order-1">
            <CardContent className="space-y-3 p-4">
              <POLineItemProductCell
                model={stock.product.model}
                description={stock.product.store_title}
                productUuid={stock.product.uuid}
                imageUrl={stock.product.image_url}
                manufacturerName={stock.product.manufacturer.short_name || stock.product.manufacturer.name}
                manufacturerUuid={stock.product.manufacturer.uuid}
                imageClassName="size-16"
              />
              <Quantities stock={stock} />
            </CardContent>
          </Card>
          <div className="max-lg:order-3">
            <StockHistory stock={stock} />
          </div>
        </div>
        <div className="min-w-0 max-lg:order-2">
          <DetailsCard stock={stock} showWarehouse={showWarehouse} />
        </div>
      </div>
    </div>
  )
}
